import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import Header from '../components/Header';
import GoogleSignInButton from '../components/GoogleSignInButton';
import PrivacyNotice from '../components/PrivacyNotice';
import { useTranslations } from '../translations';
import { ASSETS } from '../constants/assets';
import { ApiError, register, resendVerificationEmail } from '../services/auth';
import {
  fetchActiveLegalDocument,
  invalidateLegalDocumentCache,
} from '../services/legalDocuments';
import { isPasswordValid, PASSWORD_MAX_LENGTH } from '../utils/passwordValidation';
import './Register.css';

interface RegisterProps {
  onNavigateToLogin: () => void;
  onNavigateToHome: () => void;
  onNavigateToTerms: () => void;
  onNavigateToPrivacy?: () => void;
  onRegistrationComplete: () => void;
  /** Google creates the account and signs the user in within the same request. */
  onGoogleSignIn: () => void;
  draft: RegisterDraft;
  onDraftChange: (draft: RegisterDraft) => void;
}

export interface RegisterDraft {
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  };
  agreedToTerms: boolean;
  marketingEmails: boolean;
}

const INVALID_EMAIL_DEVELOPER_MESSAGE = 'Email contains forbidden character';
const EMAIL_VALIDATION_ERROR_CODE = 'Email';

const Register: React.FC<RegisterProps> = ({
  onNavigateToLogin,
  onNavigateToHome,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onRegistrationComplete,
  onGoogleSignIn,
  draft,
  onDraftChange
}) => {
  const t = useTranslations();
  const { formData, agreedToTerms, marketingEmails } = draft;
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [termsVersion, setTermsVersion] = useState<string | null>(null);
  const [termsUnavailable, setTermsUnavailable] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const loadTermsVersion = async () => {
    try {
      const terms = await fetchActiveLegalDocument('TERMS');
      setTermsVersion(terms.documentVersion);
      setTermsUnavailable(false);
    } catch {
      setTermsVersion(null);
      setTermsUnavailable(true);
    }
  };

  useEffect(() => {
    loadTermsVersion();
    // The active version is stable for the lifetime of this registration view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onDraftChange({
      ...draft,
      formData: {
        ...formData,
        [name]: value
      }
    });
    setError(null);
    const shouldClearError = name === 'password' ? isPasswordValid(value) : Boolean(value.trim());
    if (shouldClearError) {
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(name);
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = new Set<string>();
    if (!formData.firstName.trim()) errors.add('firstName');
    if (!formData.lastName.trim()) errors.add('lastName');
    if (!formData.email.trim()) errors.add('email');
    if (!isPasswordValid(formData.password)) errors.add('password');
    if (!agreedToTerms) errors.add('terms');
    if (!termsVersion) errors.add('terms');

    if (errors.size > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!termsVersion) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        termsAndConditions: agreedToTerms,
        termsVersion: termsVersion,
        marketingEmails: marketingEmails
      });

      setRegisteredEmail(formData.email);
      onRegistrationComplete();
    } catch (err) {
      if (err instanceof Error && err.message === 'DUPLICATE_EMAIL') {
        setError(t.register.duplicateEmail);
        setValidationErrors(new Set(['email']));
      } else if (err instanceof ApiError
        && err.status === 400
        && (err.hasDeveloperMessage(INVALID_EMAIL_DEVELOPER_MESSAGE)
          || err.hasFieldError('email')
          || err.hasErrorCode(EMAIL_VALIDATION_ERROR_CODE))) {
        setError(t.register.invalidEmail);
        setValidationErrors(new Set(['email']));
      } else if (err instanceof ApiError
        && err.hasErrorCode('legal_document_version_not_active')) {
        invalidateLegalDocumentCache('TERMS');
        setTermsVersion(null);
        onDraftChange({ ...draft, agreedToTerms: false });
        setValidationErrors(new Set(['terms']));
        setError(t.register.termsUpdated);
        await loadTermsVersion();
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t.register.registrationError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!registeredEmail || resendStatus === 'sending') {
      return;
    }
    setResendStatus('sending');
    try {
      await resendVerificationEmail(registeredEmail);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  if (registeredEmail) {
    return (
      <div className="register-page">
        <Header
          isLoggedIn={false}
          onNavigateToLogin={onNavigateToLogin}
          onNavigateToRegister={() => {}}
          onNavigateToHome={onNavigateToHome}
        />
        <div className="register-content">
          <div className="register-form-container">
            <div className="register-form-wrapper register-verification-state">
              <h1 className="register-title">{t.register.verifyEmailTitle}</h1>
              <p className="register-verification-description">
                {t.register.verifyEmailDescription.replace('{email}', registeredEmail)}
              </p>
              <p className="register-verification-hint">{t.register.verifyEmailHint}</p>
              <button type="button" className="btn-submit" onClick={onNavigateToLogin}>
                {t.register.loginButton}
              </button>
              <button
                type="button"
                className="register-resend-button"
                onClick={handleResendVerification}
                disabled={resendStatus === 'sending'}
              >
                {resendStatus === 'sending'
                  ? t.register.resendingVerification
                  : t.register.resendVerification}
              </button>
              {resendStatus === 'sent' && (
                <p className="register-verification-feedback">{t.register.verificationResent}</p>
              )}
              {resendStatus === 'error' && (
                <p className="error-message">{t.register.verificationResendError}</p>
              )}
            </div>
          </div>
          <div className="register-image-container">
            <img src={ASSETS.REGISTER_IMAGE} alt="" className="register-image" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      <Header
        isLoggedIn={false}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={() => {}}
        onNavigateToHome={onNavigateToHome}
      />
      <div className="register-content">
        <div className="register-form-container">
          <div className="register-form-wrapper">
            <h1 className="register-title">{t.register.title}</h1>
            
            <form className="register-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">
                    {t.register.lastName}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    className={`form-input ${validationErrors.has('lastName') ? 'form-input-error' : ''}`}
                    placeholder={t.register.lastNamePlaceholder}
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">
                    {t.register.firstName}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    className={`form-input ${validationErrors.has('firstName') ? 'form-input-error' : ''}`}
                    placeholder={t.register.firstNamePlaceholder}
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-field form-field-full">
                <label className="form-label">
                  {t.register.email}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`form-input ${validationErrors.has('email') ? 'form-input-error' : ''}`}
                  placeholder={t.register.emailPlaceholder}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field form-field-full">
                <label className="form-label">
                  {t.register.password}
                  <span className="required-asterisk">*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className={`form-input password-input ${validationErrors.has('password') ? 'form-input-error' : ''}`}
                    placeholder={t.register.passwordPlaceholder}
                    value={formData.password}
                    onChange={handleInputChange}
                    maxLength={PASSWORD_MAX_LENGTH}
                    aria-invalid={validationErrors.has('password')}
                    aria-describedby={validationErrors.has('password') ? 'register-password-error' : undefined}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t.register.hidePassword : t.register.showPassword}
                  >
                    {showPassword
                      ? <EyeOff className="password-toggle-icon" aria-hidden="true" />
                      : <Eye className="password-toggle-icon" aria-hidden="true" />}
                  </button>
                </div>
                {validationErrors.has('password') && (
                  <span id="register-password-error" className="form-field-error">
                    {t.register.passwordValidation}
                  </span>
                )}
              </div>

              <div className="terms-checkbox">
                <div className="checkbox-label">
                  <label>
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={agreedToTerms}
                      disabled={!termsVersion}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        onDraftChange({
                          ...draft,
                          agreedToTerms: isChecked
                        });
                        if (isChecked) {
                          setValidationErrors(prev => {
                            const newErrors = new Set(prev);
                            newErrors.delete('terms');
                            return newErrors;
                          });
                        }
                      }}
                    />
                    <span className={`checkbox-custom ${validationErrors.has('terms') ? 'checkbox-error' : ''}`}>
                      {agreedToTerms && (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.7728 7.33886L9.6972 14.0055C9.48831 14.28 9.16497 14.4422 8.82053 14.4444H8.81275C8.47164 14.4444 8.14942 14.2866 7.93831 14.0178L5.23609 10.5655C4.85831 10.0833 4.94275 9.38442 5.42609 9.00664C5.90942 8.62775 6.6072 8.7122 6.98609 9.19664L8.80053 11.5144L13.005 5.99331C13.3761 5.50553 14.0739 5.40997 14.5628 5.7822C15.0505 6.15442 15.145 6.85109 14.7728 7.33886Z" fill="#071105"/>
                        </svg>
                      )}
                    </span>
                  </label>
                  <span>
                    {t.register.termsAndConditions}{' '}
                    <button type="button" className="terms-link" onClick={onNavigateToTerms}>
                      {t.register.termsLink}
                    </button>{' '}
                    {t.register.termsText}
                    <span className="required-asterisk">*</span>
                  </span>
                </div>
              </div>

              <PrivacyNotice onNavigateToPrivacy={onNavigateToPrivacy} />

              <div className="marketing-checkbox">
                <div className="checkbox-label">
                  <label>
                    <input
                      type="checkbox"
                      className="checkbox-input"
                      checked={marketingEmails}
                      onChange={(e) => {
                        onDraftChange({
                          ...draft,
                          marketingEmails: e.target.checked
                        });
                      }}
                    />
                    <span className="checkbox-custom">
                      {marketingEmails && (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.7728 7.33886L9.6972 14.0055C9.48831 14.28 9.16497 14.4422 8.82053 14.4444H8.81275C8.47164 14.4444 8.14942 14.2866 7.93831 14.0178L5.23609 10.5655C4.85831 10.0833 4.94275 9.38442 5.42609 9.00664C5.90942 8.62775 6.6072 8.7122 6.98609 9.19664L8.80053 11.5144L13.005 5.99331C13.3761 5.50553 14.0739 5.40997 14.5628 5.7822C15.0505 6.15442 15.145 6.85109 14.7728 7.33886Z" fill="#071105"/>
                        </svg>
                      )}
                    </span>
                  </label>
                  <span>{t.register.marketingEmails}</span>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {termsUnavailable && (
                <div className="error-message">
                  {t.register.termsUnavailable}{' '}
                  <button type="button" className="terms-link" onClick={loadTermsVersion}>
                    {t.register.retry}
                  </button>
                </div>
              )}

              <button type="submit" className="btn-submit" disabled={!agreedToTerms || !termsVersion || isLoading}>
                {isLoading ? 'Se încarcă...' : t.register.submitButton}
              </button>
            </form>

            {/* Google signs the user straight in, so this takes the login path rather
                than the "confirm your email" path a password registration needs. */}
            <GoogleSignInButton
              variant="signup_with"
              onSuccess={onGoogleSignIn}
              onNavigateToTerms={onNavigateToTerms}
              onNavigateToPrivacy={onNavigateToPrivacy}
            />

            <div className="login-section">
              <p className="login-text">{t.register.haveAccount}</p>
              <button type="button" className="btn-login" onClick={onNavigateToLogin}>
                {t.register.loginButton}
              </button>
            </div>
          </div>
        </div>
        
        <div className="register-image-container">
          <img
            src={ASSETS.REGISTER_IMAGE}
            alt=""
            className="register-image"
          />
        </div>
      </div>
    </div>
  );
};

export default Register;
